import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UnifiedCard from '../UnifiedCard';
import TravelBoardsServiceDB from '../../services/travelBoardsServiceDB';
import { normalizeCardData, CONTENT_TYPES, CARD_CONTEXTS, getContentTypeEnum } from '../../utils/normalizeCardData';

/**
 * Shared Card Wrapper Components
 *
 * These wrappers normalize different data types and render them via UnifiedCard.
 * Use these instead of defining wrapper components in individual view files.
 */

/**
 * ActivityCardWrapper - Wrapper for activity data
 *
 * @param {Object} savedActivity - The activity data object
 * @param {Function} onManageCollections - Handler for organizing into collections
 * @param {Function} onUnsave - Handler for removing the save (a real unsave)
 * @param {Function} onSchedule - Handler for scheduling
 * @param {Function} onShare - Handler for sharing
 * @param {Function} onSave - Handler for saving (used in preview/discover contexts)
 * @param {string} context - Card context (defaults to SAVED)
 */
export const ActivityCardWrapper = ({
  savedActivity,
  onManageCollections,
  onUnsave,
  onSchedule,
  onShare,
  onSave,
  onCardClick,
  context = CARD_CONTEXTS.SAVED
}) => {
  // Normalize the activity data for UnifiedCard
  const normalizedData = useMemo(() => {
    return normalizeCardData(savedActivity, CONTENT_TYPES.ACTIVITY, context);
  }, [savedActivity, context]);

  // Build actions object.
  //
  // The delete slot is a genuine unsave. It used to be wired to an ARCHIVE
  // handler while the button read "Unsave", so a place the user believed they
  // had removed was only hidden — and nothing in the UI could bring it back.
  // Saved activities are no longer archivable; "Remove from saved" now does
  // exactly what it says.
  const actions = useMemo(() => ({
    onOrganize: onManageCollections ? () => onManageCollections(savedActivity) : undefined,
    onDelete: onUnsave ? () => onUnsave(savedActivity.id) : undefined,
    onSchedule: onSchedule ? () => onSchedule(savedActivity) : undefined,
    onShare: onShare ? () => onShare(savedActivity) : undefined,
    onSave: onSave ? () => onSave(savedActivity) : undefined
  }), [savedActivity, onManageCollections, onUnsave, onSchedule, onShare, onSave]);

  return (
    <UnifiedCard
      data={normalizedData}
      actions={actions}
      onCardClick={onCardClick}
    />
  );
};

/**
 * CollectionCardWrapper - Wrapper for collection data
 */
export const CollectionCardWrapper = ({
  collection,
  activities,
  itemCount,
  onEdit,
  onArchive,
  onDelete,
  onUnsave,
  onSaveToBoard,
  onShare,
  context = CARD_CONTEXTS.SAVED
}) => {
  const { t } = useTranslation();
  // Normalize the collection data for UnifiedCard
  const normalizedData = useMemo(() => {
    const enrichedCollection = {
      ...collection,
      preview_activities: activities?.slice(0, 6) || [],
      item_count: itemCount || collection.activity_count || 0
    };
    return normalizeCardData(enrichedCollection, CONTENT_TYPES.COLLECTION, context);
  }, [collection, activities, itemCount, context]);

  // Build actions object — use onUnsave for non-owned collections, onDelete for owned.
  // Archive is owner-only and sits alongside Delete rather than replacing it:
  // archiving is the safe "put it away" for work you don't want to destroy,
  // deleting is still there for people who mean it.
  const deleteHandler = onDelete || onUnsave;
  const actions = useMemo(() => ({
    onOrganize: onSaveToBoard ? () => onSaveToBoard(collection) : undefined,
    onArchive: onArchive ? () => onArchive(collection) : undefined,
    onDelete: deleteHandler ? () => deleteHandler(collection) : undefined,
    onShare: onShare ? () => onShare(collection) : undefined,
    deleteLabel: onUnsave ? t('cardWrappers.collectionCard.deleteLabelUnsave') : t('cardWrappers.collectionCard.deleteLabelDelete')
  }), [collection, onSaveToBoard, onArchive, deleteHandler, onUnsave, onShare, t]);

  return (
    <UnifiedCard
      data={normalizedData}
      actions={actions}
    />
  );
};

/**
 * WalkingTourCardWrapper - Wrapper for walking tour data
 */
export const WalkingTourCardWrapper = ({
  walkingTour,
  onUnsave,
  onOrganize,
  onShare,
  currentUserId,
  context = CARD_CONTEXTS.SAVED
}) => {
  const { t } = useTranslation();
  // Normalize the walking tour data for UnifiedCard
  const normalizedData = useMemo(() => {
    return normalizeCardData(walkingTour, CONTENT_TYPES.WALKING_TOUR, context);
  }, [walkingTour, context]);

  const isOwner = currentUserId && walkingTour.ownerId === currentUserId;

  // Build actions object
  const actions = useMemo(() => ({
    onOrganize: onOrganize ? () => onOrganize(walkingTour) : undefined,
    onDelete: onUnsave ? () => onUnsave(walkingTour) : undefined,
    onShare: onShare ? () => onShare(walkingTour) : undefined,
    deleteLabel: isOwner ? t('cardWrappers.walkingTourCard.deleteLabelDelete') : t('cardWrappers.walkingTourCard.deleteLabelUnsave')
  }), [walkingTour, onUnsave, onOrganize, onShare, isOwner, t]);

  return (
    <UnifiedCard
      data={normalizedData}
      actions={actions}
    />
  );
};

/**
 * TourismExperienceCardWrapper - Wrapper for tourism experience data
 */
export const TourismExperienceCardWrapper = ({
  experience,
  onUnsave,
  onOrganize,
  onShare,
  context = CARD_CONTEXTS.SAVED
}) => {
  const normalizedData = useMemo(() => {
    return normalizeCardData(experience, CONTENT_TYPES.TOURISM_EXPERIENCE, context);
  }, [experience, context]);

  const actions = useMemo(() => ({
    onOrganize: onOrganize ? () => onOrganize(experience) : undefined,
    onDelete: onUnsave ? () => onUnsave(experience) : undefined,
    onShare: onShare ? () => onShare(experience) : undefined
  }), [experience, onUnsave, onOrganize, onShare]);

  return (
    <UnifiedCard
      data={normalizedData}
      actions={actions}
    />
  );
};

/**
 * TripCardWrapper - Wrapper for trip data
 */
export const TripCardWrapper = ({
  savedTrip,
  activityCount,
  onOrganize,
  onUnsave,
  onShare,
  onViewActivities,
  context = CARD_CONTEXTS.SAVED
}) => {
  // Normalize the trip data for UnifiedCard
  const normalizedData = useMemo(() => {
    const enrichedTrip = {
      ...savedTrip,
      activity_count: activityCount
    };
    return normalizeCardData(enrichedTrip, CONTENT_TYPES.TRIP, context);
  }, [savedTrip, activityCount, context]);

  // Build actions object
  const actions = useMemo(() => ({
    onOrganize: onOrganize ? () => onOrganize(savedTrip) : undefined,
    // The trip id, as All's trip card sends it: handleUnsaveTrip and the hook
    // take an id, and removing a whole object matched nothing (BH-21-01).
    onDelete: onUnsave ? () => onUnsave(savedTrip.id) : undefined,
    onShare: onShare ? () => onShare(savedTrip) : undefined,
    onViewSavedActivities: onViewActivities || undefined
  }), [savedTrip, onOrganize, onUnsave, onShare, onViewActivities]);

  return (
    <UnifiedCard
      data={normalizedData}
      actions={actions}
    />
  );
};

/**
 * AlbumCardWrapper - Wrapper for album data
 */
// Albums are not archivable: trip_albums.trip_id is NOT NULL, so every album
// already lives on its trip and on the Planner Past tab, and it carries its own
// status + visibility controls. The onArchive prop this used to accept was
// never passed through to actions — it did nothing — and is now gone.
//
// The card's remove action is one of two different things, and the caller
// picks by which prop it passes: onDelete is the author's permanent delete (the
// trip Recap tab), onUnsave removes a save (My Saved Items, where every album is
// a save — your own included). Only a real delete is labelled "Delete"; an
// unsave keeps ActionRow's default "Unsave".
export const AlbumCardWrapper = ({
  album,
  onSaveToBoard,
  onDelete,
  onUnsave,
  onShare,
  context = CARD_CONTEXTS.SAVED
}) => {
  const { t } = useTranslation();

  // Normalize the album data for UnifiedCard
  const normalizedData = useMemo(() => {
    return normalizeCardData(album, CONTENT_TYPES.ALBUM, context);
  }, [album, context]);

  // Build actions object
  const removeHandler = onDelete || onUnsave;
  const actions = useMemo(() => ({
    onOrganize: onSaveToBoard ? () => onSaveToBoard(album) : undefined,
    onDelete: removeHandler ? () => removeHandler(album) : undefined,
    deleteLabel: onDelete ? t('cardWrappers.albumCard.deleteLabelDelete') : undefined,
    onShare: onShare ? () => onShare(album) : undefined
  }), [album, onSaveToBoard, removeHandler, onDelete, onShare, t]);

  return (
    <UnifiedCard
      data={normalizedData}
      actions={actions}
    />
  );
};

/**
 * BoardCardWrapper - Wrapper for travel board data
 */
export const BoardCardWrapper = ({
  board,
  onEdit,
  onArchive,
  onDelete,
  onShare,
  onLike,
  onSave,
  isLiked = false,
  isSaved = true,
  onClick,
  context = CARD_CONTEXTS.SAVED
}) => {
  const { t } = useTranslation();

  // A board that came with no preview (the saves summary's bulk read failed,
  // C358(4)) asks for its own, once, through the same listing gate the bulk
  // read uses (C375(2)). An empty array is an answer and is never re-asked
  // (C347). Meanwhile the card shows its neutral loading state; if this read
  // fails too, the card shows without previews — absent, never "empty".
  const boardId = board?.id;
  const needsPreview = !!boardId
    && !Array.isArray(board?.preview_items) && !Array.isArray(board?.previewDetails);
  // { boardId, items }: items is the answer, or null when the read failed
  const [fetchedPreview, setFetchedPreview] = useState(null);
  useEffect(() => {
    if (!needsPreview) return undefined;
    let cancelled = false;
    const settle = (items) => { if (!cancelled) setFetchedPreview({ boardId, items }); };
    TravelBoardsServiceDB.getBoardPreview(boardId, { surface: 'list' })
      .then(result => settle(result?.success && Array.isArray(result.preview_items) ? result.preview_items : null))
      .catch(() => settle(null));
    return () => { cancelled = true; };
  }, [boardId, needsPreview]);
  const fetched = fetchedPreview && fetchedPreview.boardId === boardId ? fetchedPreview : null;
  const previewLoading = needsPreview && !fetched;

  // Normalize the board data for UnifiedCard
  const normalizedData = useMemo(() => {
    const source = needsPreview && fetched?.items
      ? { ...board, preview_items: fetched.items }
      : board;
    return normalizeCardData(source, CONTENT_TYPES.BOARD, context);
  }, [board, context, needsPreview, fetched]);

  // Build actions object. onDelete is the owner's permanent delete (it opens
  // "Delete Board"), so it needs its own label — without one, ActionRow's
  // default "Unsave" made deleting a board read like removing a bookmark.
  const actions = useMemo(() => ({
    onEdit: onEdit ? () => onEdit(board) : undefined,
    onArchive: onArchive ? () => onArchive(board) : undefined,
    onDelete: onDelete ? () => onDelete(board) : undefined,
    deleteLabel: onDelete ? t('cardWrappers.boardCard.deleteLabelDelete') : undefined,
    onShare: onShare ? () => onShare(board) : undefined,
    onLike: onLike ? () => onLike(board) : undefined,
    onSave: onSave ? () => onSave(board) : undefined
  }), [board, onEdit, onArchive, onDelete, onShare, onLike, onSave, t]);

  return (
    <UnifiedCard
      data={normalizedData}
      isLoading={previewLoading}
      actions={actions}
      onCardClick={onClick}
    />
  );
};

/**
 * ArchivedCardWrapper - Wrapper for archived collections and boards.
 *
 * Only these two types are archivable. Activities use "Remove from saved" and
 * albums live on their trip, so neither reaches this wrapper any more.
 */
export const ArchivedCardWrapper = ({
  item,
  type,
  onRestore,
  onDelete
}) => {
  const { t } = useTranslation();
  const contentType = getContentTypeEnum(type);

  // Transform archived item to normalized format
  const transformedData = useMemo(() => {
    switch (type) {
      // visibility MUST be carried through. normalizeCardData defaults a
      // missing value to 'private', so omitting it made every archived card
      // claim the item was private — the exact confusion this whole model
      // exists to prevent. Archiving does not touch visibility unless the user
      // ticked "Also make it private", and the badge has to say so.
      case 'collection':
        return {
          id: item.id,
          name: item.name,
          description: t('cardWrappers.archivedCard.activityCount', { count: item.activityCount || 0 }),
          color: item.color,
          visibility: item.visibility,
          archived_at: item.archivedAt || item.archived_at
        };
      case 'board':
        return {
          id: item.id,
          name: item.name,
          description: item.description,
          cover_image: item.cover_image,
          visibility: item.visibility,
          archived_at: item.archivedAt || item.archived_at
        };
      default:
        return item;
    }
  }, [item, type, t]);

  // Normalize the data
  const normalizedData = useMemo(() => {
    const data = normalizeCardData(transformedData, contentType, CARD_CONTEXTS.SAVED);
    // Drives the "Archived" badge
    return { ...data, isArchived: true };
  }, [transformedData, contentType]);

  // Restore, and delete for anyone who decides they're done with it after all.
  // Both handlers take the item so the caller can name it in a confirm dialog.
  // deleteLabel is explicit because ActionRow's default is "Unsave", which is
  // wrong for something you own — from here it is a permanent delete.
  const actions = useMemo(() => ({
    onRestore: onRestore ? () => onRestore(item) : undefined,
    onDelete: onDelete ? () => onDelete(item) : undefined,
    deleteLabel: t('cardWrappers.archivedCard.deleteLabel')
  }), [item, onRestore, onDelete, t]);

  return (
    <UnifiedCard
      data={normalizedData}
      actions={actions}
    />
  );
};
